// btc15/indicators.js — the desk's four readings, drawn live.
//
// The user, 2026-09-16: "none of these charts are on the precise tab —
// implement those indicators as well". Correct: the glossary was drawing
// curves that existed nowhere. These are the real thing. The tab renders
// them against the live book, and the glossary renders the SAME components,
// so the picture in the explanation is the widget on the tab rather than an
// illustration of it.
//
// Four readings, chosen because each answers a question the tab otherwise
// leaves to arithmetic the user cannot see:
//
//   Cost      what the fee takes, and why cheap contracts are the dear ones
//   Odds      where a wandering price can finish, and this window's z
//   Decided   how much of the outcome is already fixed at this minute
//   Payoff    what Ⓩ5 puts up against what it gets back
//
// All four are pure functions of their props. No fetching, no state.
import { kalshiFee, gapZ, phi } from "./math";
import { knownSoFarAt } from "./study";

const W = 240, H = 96;
const BASE = 74, LEFT = 18, RIGHT = 226, TOP = 12;
const ENTRY_FLOOR = 0.70;

const fin = (v) => Number.isFinite(Number(v));
const sx = (t) => LEFT + Math.max(0, Math.min(1, t)) * (RIGHT - LEFT);
const sy = (v, max = 1) => BASE - Math.max(0, Math.min(1, v / max)) * (BASE - TOP);
const normal = (z) => Math.exp(-0.5 * z * z);

function poly(fn, max = 1, steps = 56, from = 0, to = 1) {
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = from + ((to - from) * i) / steps;
    const v = fn(t);
    if (!fin(v)) continue;
    const y = sy(v, max);
    if (!fin(y)) continue;
    pts.push(`${sx(t).toFixed(1)},${y.toFixed(1)}`);
  }
  return pts.join(" ");
}

function Frame({ P, title, right, children, testId, label }) {
  return (
    <div data-testid={testId} style={{ minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
        <span style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", color: P.muted }}>{title}</span>
        {right ? <span style={{ fontSize: 10.5, fontWeight: 800, fontFamily: "ui-monospace, Menlo, monospace", color: P.text }}>{right}</span> : null}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label || title}
        style={{ display: "block", width: "100%", height: "auto", background: P.surface, border: `1px solid ${P.border}`, borderRadius: 8 }}>
        <title>{label || title}</title>
        {children}
      </svg>
    </div>
  );
}

// ── 1. what a contract costs ──────────────────────────────────────────────
// Two curves on one axis. The dashed hump is the fee per contract — largest
// at a coin flip. The solid line is the one that decides behaviour: the fee
// as a SHARE of the stake, 0.07·(1−p), which falls all the way to the right.
// The shaded block is everything the desk refuses to buy.
export function CostCurve({ palette: P, yesAsk, floor = ENTRY_FLOOR, compact }) {
  const ask = fin(yesAsk) ? Number(yesAsk) : null;
  const share = (p) => 0.07 * (1 - p);
  const perContract = (p) => 0.07 * p * (1 - p);
  const MAX = 0.07;
  return (
    <Frame P={P} testId="btc15-ind-cost" title="Cost of being wrong-priced"
      label="Fee as a share of stake against contract price, with the entry floor"
      right={ask != null ? `${(share(ask) * 100).toFixed(2)}% of stake` : null}>
      <rect x={LEFT} y={TOP} width={sx(floor) - LEFT} height={BASE - TOP} fill={P.red} opacity="0.10" />
      <line x1={sx(floor)} y1={TOP} x2={sx(floor)} y2={BASE} stroke={P.green} strokeWidth="1.4" strokeDasharray="3 2" />
      <text x={sx(floor) + 3} y={TOP + 8} fill={P.green} fontSize="8" fontWeight="800">{Math.round(floor * 100)}¢ floor</text>
      <text x={(LEFT + sx(floor)) / 2} y={TOP + 10} fill={P.red} fontSize="8" fontWeight="800" textAnchor="middle">refused</text>
      <polyline points={poly(perContract, MAX)} fill="none" stroke={P.muted} strokeWidth="1.2" strokeDasharray="3 2" />
      <polyline points={poly(share, MAX)} fill="none" stroke={P.red} strokeWidth="2" />
      {ask != null && (
        <>
          <circle cx={sx(ask)} cy={sy(share(ask), MAX)} r="3.6" fill={P.red} />
          <line x1={sx(ask)} y1={sy(share(ask), MAX)} x2={sx(ask)} y2={BASE} stroke={P.red} strokeWidth="1" opacity="0.5" />
          <text x={sx(ask)} y={BASE - 3} fill={P.red} fontSize="8" fontWeight="800"
            textAnchor={ask > 0.82 ? "end" : ask < 0.12 ? "start" : "middle"}>{Math.round(ask * 100)}¢</text>
        </>
      )}
      {!compact && <text x={LEFT + 2} y={TOP + 20} fill={P.red} fontSize="7.5">share of stake</text>}
      {!compact && <text x={RIGHT - 2} y={TOP + 30} fill={P.muted} fontSize="7.5" textAnchor="end">per contract</text>}
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
      <text x={LEFT} y={BASE + 11} fill={P.faint} fontSize="8">0¢</text>
      <text x={RIGHT} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="end">100¢</text>
    </Frame>
  );
}

// ── 2. the odds the model actually sees ───────────────────────────────────
// The distribution of where a driftless price could finish, with the target
// at the middle and this window's z marked. The shaded side is the share of
// paths that win — which IS the fair value.
export function OddsCurve({ palette: P, btc, target, minute, sigma15, marketP, compact }) {
  const ok = fin(btc) && fin(target) && fin(sigma15) && Number(sigma15) > 0;
  const z = ok ? gapZ(Number(btc), Number(target), Number(minute) || 0, Number(sigma15)) : 0;
  const zt = Math.max(0.03, Math.min(0.97, 0.5 + z / 6));           // ±3σ across the box
  const fv = ok ? phi(z) : null;                                     // the desk's own Φ, not an approximation
  return (
    <Frame P={P} testId="btc15-ind-odds" title="Where it can finish"
      label="The spread of possible settlements, with this window's gap marked"
      right={ok ? `z ${z.toFixed(2)}` : null}>
      <polygon points={`${sx(0.5)},${BASE} ${poly((t) => normal((t - 0.5) * 6), 1, 40, 0.5, 1)} ${RIGHT},${BASE}`}
        fill={P.green} opacity="0.22" />
      <polygon points={`${LEFT},${BASE} ${poly((t) => normal((t - 0.5) * 6), 1, 40, 0, 0.5)} ${sx(0.5)},${BASE}`}
        fill={P.red} opacity="0.16" />
      <polyline points={poly((t) => normal((t - 0.5) * 6))} fill="none" stroke={P.muted} strokeWidth="1.5" />
      <line x1={sx(0.5)} y1={TOP} x2={sx(0.5)} y2={BASE} stroke={P.text} strokeWidth="1.2" strokeDasharray="3 2" />
      <text x={sx(0.5)} y={TOP - 2} fill={P.text} fontSize="8" textAnchor="middle">target</text>
      {ok && (
        <>
          <line x1={sx(zt)} y1={TOP} x2={sx(zt)} y2={BASE} stroke={z >= 0 ? P.green : P.red} strokeWidth="2" />
          <circle cx={sx(zt)} cy={sy(normal(z))} r="3.4" fill={z >= 0 ? P.green : P.red} />
          <text x={sx(zt)} y={BASE + 11} fill={z >= 0 ? P.green : P.red} fontSize="8" fontWeight="800"
            textAnchor={zt > 0.85 ? "end" : zt < 0.15 ? "start" : "middle"}>{Math.abs(z) >= 3 ? `now · ${z > 0 ? "far above" : "far below"}` : "now"}</text>
        </>
      )}
      {!compact && fv != null && (
        <text x={RIGHT - 2} y={TOP + 10} fill={P.green} fontSize="8" fontWeight="800" textAnchor="end">
          model {Math.round(fv * 100)}%{fin(marketP) ? ` · mkt ${Math.round(Number(marketP) * 100)}%` : ""}
        </text>
      )}
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
      {!(ok && zt < 0.2) && <text x={LEFT} y={BASE + 11} fill={P.faint} fontSize="8">below</text>}
      {!(ok && zt > 0.8) && <text x={RIGHT} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="end">above</text>}
    </Frame>
  );
}

// ── 3. how much is already settled ────────────────────────────────────────
// The study's curve across the fifteen minutes, with the current minute on
// it. Shown, never traded on — the market prices it too.
export function DecidedCurve({ palette: P, minute, knownSoFar, compact }) {
  const m = fin(minute) ? Math.max(0, Math.min(15, Number(minute))) : null;
  const f = (t) => knownSoFarAt(t * 15);                            // the study's table, interpolated — not a made-up curve
  const t0 = m == null ? null : m / 15;
  return (
    <Frame P={P} testId="btc15-ind-decided" title="Already decided"
      label="Share of windows whose direction is fixed by each minute"
      right={fin(knownSoFar) ? `${Math.round(Number(knownSoFar))}%` : null}>
      <polyline points={poly(f)} fill="none" stroke={P.accent} strokeWidth="2" />
      <polygon points={`${LEFT},${BASE} ${poly(f)} ${RIGHT},${BASE}`} fill={P.accent} opacity="0.12" />
      {t0 != null && (
        <>
          <line x1={sx(t0)} y1={TOP} x2={sx(t0)} y2={BASE} stroke={P.text} strokeWidth="1.5" strokeDasharray="3 2" />
          <circle cx={sx(t0)} cy={sy(f(t0))} r="3.6" fill={P.accent} />
          <text x={sx(t0)} y={TOP - 2} fill={P.text} fontSize="8" fontWeight="800"
            textAnchor={t0 > 0.85 ? "end" : t0 < 0.15 ? "start" : "middle"}>m{Math.round(m)}</text>
        </>
      )}
      {!compact && !(m != null && m > 11.5) && <text x={RIGHT - 2} y={sy(0.93) - 3} fill={P.faint} fontSize="7.5" textAnchor="end">91% by m13</text>}
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
      <text x={LEFT} y={BASE + 11} fill={P.faint} fontSize="8">m0 · 50%</text>
      <text x={RIGHT} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="end">bell</text>
    </Frame>
  );
}

// ── 4. what the stake actually does ───────────────────────────────────────
// Outlay against return, both bars drawn to the same scale, with the fee
// shown as the slice of the outlay it really is.
export function PayoffBars({ palette: P, yesAsk, stake = 5, currency = "zbucks", compact }) {
  const ask = fin(yesAsk) ? Number(yesAsk) : null;
  const sym = currency === "usd" ? "$" : "Ⓩ";
  if (ask == null || ask <= 0) {
    return (
      <Frame P={P} testId="btc15-ind-payoff" title="What a stake returns" label="Outlay against return">
        <text x={W / 2} y={BASE / 2} fill={P.faint} fontSize="9" textAnchor="middle">no quote on the book</text>
      </Frame>
    );
  }
  const s = Number(stake) || 5;
  const n = s / ask;
  const fee = kalshiFee(ask, n);
  const out = s + fee;
  const max = Math.max(n, out) * 1.12;
  const bw = 46;
  const net = n - out;
  return (
    <Frame P={P} testId="btc15-ind-payoff" title="What a stake returns"
      label="Outlay against return for one stake at the current price"
      right={`${net >= 0 ? "+" : "−"}${sym}${Math.abs(net).toFixed(2)} net`}>
      {/* outlay: stake + fee stacked */}
      <rect x={LEFT + 16} y={sy(s / max)} width={bw} height={BASE - sy(s / max)} fill={P.accent} opacity="0.75" rx="2" />
      <rect x={LEFT + 16} y={sy(out / max)} width={bw} height={sy(s / max) - sy(out / max)} fill={P.red} opacity="0.9" rx="2" />
      <text x={LEFT + 16 + bw / 2} y={sy(out / max) - 3} fill={P.text} fontSize="8.5" fontWeight="800" textAnchor="middle">{`${sym}${out.toFixed(2)}`}</text>
      <text x={LEFT + 16 + bw / 2} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="middle">you put up</text>
      {/* return if right */}
      <rect x={LEFT + 96} y={sy(n / max)} width={bw} height={BASE - sy(n / max)} fill={P.green} opacity="0.8" rx="2" />
      <text x={LEFT + 96 + bw / 2} y={sy(n / max) - 3} fill={P.green} fontSize="8.5" fontWeight="800" textAnchor="middle">{`${sym}${n.toFixed(2)}`}</text>
      <text x={LEFT + 96 + bw / 2} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="middle">if right</text>
      {/* nothing at all if wrong */}
      <rect x={LEFT + 176} y={BASE - 3} width={bw} height={3} fill={P.red} rx="1" />
      <text x={LEFT + 176 + bw / 2} y={BASE - 7} fill={P.red} fontSize="8.5" fontWeight="800" textAnchor="middle">{sym}0</text>
      <text x={LEFT + 176 + bw / 2} y={BASE + 11} fill={P.faint} fontSize="8" textAnchor="middle">if wrong</text>
      {!compact && <text x={LEFT + 16 + bw + 4} y={sy(out / max) + 7} fill={P.red} fontSize="7.5">fee</text>}
      <line x1={LEFT} y1={BASE} x2={RIGHT} y2={BASE} stroke={P.border} />
    </Frame>
  );
}

// After the bell the book is 100/0 and every reading degenerates — a fee of
// 0.00%, a z of 14.7, a payoff of Ⓩ5 for Ⓩ5. Seen on the tab 2026-09-16.
// None of that is information, so a settled window says so instead.
export function isSettled({ minute, yesAsk, yesBid }) {
  const m = Number(minute);
  if (Number.isFinite(m) && m >= 15) return true;
  // Number(null) is 0, which would read a MISSING quote as a 0¢ ask and call
  // an unquoted window settled. An absent quote is an absent quote.
  const quoted = (v) => v != null && v !== "" && Number.isFinite(Number(v));
  const a = quoted(yesAsk) ? Number(yesAsk) : null, b = quoted(yesBid) ? Number(yesBid) : null;
  return (a != null && (a >= 0.995 || a <= 0.005)) || (b != null && (b >= 0.995 || b <= 0.005));
}

function Settled({ P, testId, title }) {
  return (
    <Frame P={P} testId={testId} title={title} label={`${title} — window settled`}>
      <text x={W / 2} y={BASE / 2 + 2} fill={P.muted} fontSize="9.5" fontWeight="700" textAnchor="middle">window settled</text>
      <text x={W / 2} y={BASE / 2 + 15} fill={P.faint} fontSize="8" textAnchor="middle">the book is decided — nothing left to price</text>
    </Frame>
  );
}

export const INDICATORS = { cost: CostCurve, odds: OddsCurve, decided: DecidedCurve, payoff: PayoffBars };

/** The whole strip, as the tab shows it. */
export default function Indicators({ palette, yesAsk, btc, target, minute, sigma15, marketP, knownSoFar, stake, currency, compact }) {
  const P = palette || {};
  if (isSettled({ minute, yesAsk })) {
    return (
      <div data-testid="btc15-indicators" data-settled="true" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
        <Settled P={P} testId="btc15-ind-cost" title="Cost of being wrong-priced" />
        <Settled P={P} testId="btc15-ind-odds" title="Where it can finish" />
        <DecidedCurve palette={P} minute={15} knownSoFar={100} compact={compact} />
        <Settled P={P} testId="btc15-ind-payoff" title="What a stake returns" />
      </div>
    );
  }
  return (
    <div data-testid="btc15-indicators" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
      <CostCurve palette={P} yesAsk={yesAsk} compact={compact} />
      <OddsCurve palette={P} btc={btc} target={target} minute={minute} sigma15={sigma15} marketP={marketP} compact={compact} />
      <DecidedCurve palette={P} minute={minute} knownSoFar={knownSoFar} compact={compact} />
      <PayoffBars palette={P} yesAsk={yesAsk} stake={stake} currency={currency} compact={compact} />
    </div>
  );
}
